import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { BsChatDotsFill, BsX, BsSend, BsPersonFill } from "react-icons/bs";
import BASE_URL from "../../config/api";

const SOCKET_URL = BASE_URL;

function generateSessionId() {
  const stored = localStorage.getItem("ps_chat_session");
  if (stored) return stored;
  const id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  localStorage.setItem("ps_chat_session", id);
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);
  const sessionId = useRef(generateSessionId());
  const chatInfo = useRef(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ["websocket"], autoConnect: false });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      // Re-join the session room after any reconnect
      const info = chatInfo.current;
      if (info) socket.emit("chat:start", { sessionId: sessionId.current, ...info });
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    socket.on("chat:history", (history) => setMessages(history));

    socket.on("chat:message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      if (msg.sender === "admin" && !open) {
        setUnread((u) => u + 1);
      }
    });

    socket.on("chat:typing", ({ sender }) => {
      if (sender === "admin") {
        setAdminTyping(true);
        setTimeout(() => setAdminTyping(false), 2000);
      }
    });

    socket.on("chat:closed", () => {
      setMessages((prev) => [
        ...prev,
        { sender: "admin", text: "This chat has been closed. Thank you!", timestamp: new Date(), read: true },
      ]);
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (open) {
      setUnread(0);
      const socket = socketRef.current;
      if (socket && !socket.connected) socket.connect();
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, adminTyping]);

  const startChat = () => {
    if (!visitorName.trim()) return;
    const socket = socketRef.current;
    chatInfo.current = { visitorName: visitorName.trim(), visitorPhone: visitorPhone.trim() };
    if (socket.connected) {
      socket.emit("chat:start", { sessionId: sessionId.current, ...chatInfo.current });
    } else {
      socket.connect(); // chat:start is emitted from the connect handler
    }
    setStarted(true);
  };

  const sendMessage = () => {
    const text = input.trim();
    if (!text || !socketRef.current) return;
    socketRef.current.emit("chat:message", {
      sessionId: sessionId.current,
      text,
      sender: "visitor",
    });
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        socketRef.current?.emit("chat:typing", { sessionId: sessionId.current, sender: "visitor" });
      }, 300);
    }
  };

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 font-Montserrat">
      {/* Chat panel */}
      {open && (
        <div className="w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          style={{ height: "480px" }}>

          {/* Header */}
          <div className="bg-[#1877F2] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <BsPersonFill className="text-white text-lg" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Perfect Shelters</p>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-300" : "bg-gray-300"}`} />
                  <p className="text-white/80 text-[11px]">{connected ? "Online" : "Connecting..."}</p>
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white transition-colors p-1">
              <BsX className="text-xl" />
            </button>
          </div>

          {/* Body */}
          {!started ? (
            <div className="flex-1 flex flex-col justify-center px-6 py-8 gap-4">
              <div className="text-center mb-2">
                <p className="font-bold text-gray-900 text-base">Start a conversation</p>
                <p className="text-gray-500 text-xs mt-1">We typically reply within minutes</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Your name *</label>
                <input
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && startChat()}
                  placeholder="Enter your name"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone (optional)</label>
                <input
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && startChat()}
                  placeholder="+234..."
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
                />
              </div>
              <button
                onClick={startChat}
                disabled={!visitorName.trim()}
                className="w-full bg-[#1877F2] hover:bg-[#0e5fc7] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-colors duration-200 text-sm mt-1"
              >
                Start Chat
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                {messages.length === 0 && (
                  <p className="text-center text-gray-400 text-xs mt-8">
                    Say hello! We're here to help.
                  </p>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.sender === "visitor"
                          ? "bg-[#1877F2] text-white rounded-br-sm"
                          : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"
                      }`}
                    >
                      <p>{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === "visitor" ? "text-white/60 text-right" : "text-gray-400"}`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                {adminTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t border-gray-100 bg-white flex gap-2 flex-shrink-0">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]/30 focus:border-[#1877F2]"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim()}
                  className="w-10 h-10 flex items-center justify-center bg-[#1877F2] hover:bg-[#0e5fc7] disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-white transition-colors flex-shrink-0"
                  aria-label="Send message"
                >
                  <BsSend className="text-sm" />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bubble button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="w-14 h-14 bg-[#1877F2] hover:bg-[#0e5fc7] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
      >
        {open ? <BsX className="text-2xl" /> : <BsChatDotsFill className="text-xl" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
    </div>
  );
}
